import React, { useEffect, useState } from 'react';
import { Checkbox, Dialog } from '@mui/material';
import { Button, IconGraphy, Loading } from '@leapeasy/ui-kit';
import styled from 'styled-components';
import { Layerbar } from 'components/common/Layerbar';
import { CancelDialog } from 'components/common/Dialog/CancelDialog';
import Img_1 from 'assets/img_1.png';
import {
  POLICY_CANCELLATION_INTRO,
  POLICY_CANCELLATION_STATEMENTS,
} from './policyCancellationContent';

/**
 * Two-step policy cancellation UX (agreement layer + final warning), matching prod.
 */
export function PolicyCancellationFlow({
  open,
  policy,
  onClose,
  onConfirmCancel,
  isSubmitting = false,
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (!open) {
      setAcknowledged(false);
      setShowWarning(false);
    }
  }, [open]);

  if (!open || !policy) return null;

  const riderId = policy.rider_id;
  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <>
      <Layerbar>
        <Panel>
          {isSubmitting && (
            <LoadingOverlay aria-live="polite" aria-busy="true">
              <Loading size="large" label="Cancelling policy…" />
            </LoadingOverlay>
          )}
          <PanelHeader>
            <span>Policy Cancellation</span>
            <IconGraphy
              icon="General.Clear"
              onClick={handleClose}
              style={{
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.4 : 1,
              }}
            />
          </PanelHeader>
          <PanelBody>
            <p>{POLICY_CANCELLATION_INTRO}</p>
            <ul>
              {POLICY_CANCELLATION_STATEMENTS.map((statement) => (
                <li key={statement}>{statement}</li>
              ))}
            </ul>
            <CheckboxRow>
              <Checkbox
                disableRipple
                disabled={isSubmitting}
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
                sx={{
                  color: '#69236b',
                  '&.Mui-checked .MuiSvgIcon-root': {
                    borderColor: '#69236b',
                    color: '#69236b',
                  },
                }}
              />
              <span>I acknowledge</span>
            </CheckboxRow>
          </PanelBody>
          <PanelFooter>
            <Button
              size="medium"
              variant="tertiary"
              style={{ flexShrink: 2 }}
              disabled={isSubmitting}
              onClick={handleClose}
            >
              Close
            </Button>
            <Button
              size="medium"
              variant="primary"
              style={{ flexShrink: 2 }}
              disabled={!acknowledged || isSubmitting}
              onClick={() => setShowWarning(true)}
            >
              Confirm Policy Cancellation
            </Button>
          </PanelFooter>
        </Panel>
      </Layerbar>

      <Dialog open={showWarning} onClose={() => !isSubmitting && setShowWarning(false)}>
        <CancelDialog
          img={Img_1}
          title="Are you sure you want to cancel this policy?"
          detail={`Policy ${riderId || ''} — This action cannot be undone and you will no longer be able to file a claim.`}
          noFunc={() => !isSubmitting && setShowWarning(false)}
          yesFunc={() => {
            setShowWarning(false);
            onConfirmCancel();
          }}
          isSubmitting={isSubmitting}
          yesLabel="Yes, please cancel"
          noLabel="No, don't cancel"
          confirmVariant="destructive"
        />
      </Dialog>
    </>
  );
}

const Panel = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.82);
  border-radius: 8px;
`;

const PanelHeader = styled.div`
  width: 100%;
  display: flex;
  padding: 16px 0;
  border-bottom: 1px solid rgba(234, 234, 234, 1);
  align-items: center;
  justify-content: space-between;
  color: rgba(46, 15, 64, 1);
  font-size: 20px;
  font-weight: 500;

  svg {
    cursor: pointer;
  }
`;

const PanelBody = styled.div`
  color: rgba(106, 94, 113, 1);
  font-weight: 400;
  font-size: 14px;
  margin-top: 24px;
  flex: 1;
  width: 100%;
  overflow: auto;

  ul {
    padding: 20px;
    margin: 0;
  }

  p {
    margin: 0;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
  padding-top: 16px;
`;
